import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { ExternalLinkIcon, ArrowLeftIcon, GitForkIcon, CopyIcon, ShareIcon, PencilIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Layout } from '@/components/Layout';
import { useAuthor } from '@/hooks/useAuthor';
import { useLinkList } from '@/hooks/useLinkLists';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getKindConfig } from '@/lib/nip51';
import type { LinkItem } from '@/hooks/useLinkLists';

interface LinkListPageProps {
  pubkey: string;
  identifier: string;
  kind?: number;
}

function ItemRow({ item }: { item: LinkItem }) {
  const isLink = item.tag === 'r';

  const display = (() => {
    if (item.tag === 'r') return item.label || item.value;
    if (item.tag === 'p') {
      try { return nip19.npubEncode(item.value); } catch { return item.value; }
    }
    if (item.tag === 'e') {
      try { return nip19.noteEncode(item.value); } catch { return item.value; }
    }
    if (item.tag === 't') return `#${item.value}`;
    if (item.tag === 'relay') return item.value;
    if (item.tag === 'word') return item.value;
    if (item.tag === 'a') return item.value;
    return item.value;
  })();

  const subtitle = item.tag !== 'r' ? item.value : item.value;

  if (isLink) {
    return (
      <a href={item.value} target="_blank" rel="noopener noreferrer" className="block">
        <Card className="transition-all hover:shadow-md hover:border-primary/30 hover:scale-[1.01] active:scale-[0.99]">
          <CardContent className="py-4 px-5 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{display}</p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>
            </div>
            <ExternalLinkIcon className="size-4 text-muted-foreground shrink-0" />
          </CardContent>
        </Card>
      </a>
    );
  }

  return (
    <Card className="transition-all hover:shadow-md">
      <CardContent className="py-4 px-5 flex items-center gap-3">
        <Badge variant="secondary" className="text-xs shrink-0 uppercase">{item.tag}</Badge>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-mono truncate">{display}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function LinkListPage({ pubkey, identifier, kind = 30003 }: LinkListPageProps) {
  const author = useAuthor(pubkey);
  const { data: list, isLoading } = useLinkList(pubkey, identifier, kind);
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  const npub = nip19.npubEncode(pubkey);
  const displayName = author.data?.metadata?.name ?? npub.slice(0, 12) + '...';
  const kindConfig = getKindConfig(kind);
  const isOwner = user?.pubkey === pubkey;

  useSeoMeta({
    title: list ? `${list.title} — Nostr Links` : `${kindConfig?.name ?? 'List'} — Nostr Links`,
    description: list?.description ?? `A ${kindConfig?.name ?? 'list'} by ${displayName}`,
  });

  const handleFork = () => {
    if (!user) {
      toast({ title: 'Sign in required', description: 'You need to sign in to fork a list.' });
      return;
    }
    if (list) {
      navigate('/create', { state: { forkFrom: list } });
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({ title: 'Link copied', description: 'Shareable link copied to clipboard.' });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container max-w-2xl py-8">
          <Skeleton className="h-6 w-32 mb-6" />
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-full mb-8" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!list) {
    return (
      <Layout>
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">List Not Found</h1>
          <p className="text-muted-foreground mb-6">
            This list doesn&apos;t exist or has been deleted.
          </p>
          <Button asChild variant="outline">
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        {/* Back link */}
        <Link
          to={`/${npub}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back to profile
        </Link>

        {/* List header */}
        <div className="mb-8">
          {list.image && (
            <div className="w-full h-40 rounded-xl overflow-hidden mb-4">
              <img src={list.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}

          {/* Kind label */}
          {kindConfig && (
            <p className="text-sm text-muted-foreground mb-1">
              {kindConfig.icon} {kindConfig.name}
            </p>
          )}

          <h1 className="text-2xl font-bold">{list.title || kindConfig?.name || 'List'}</h1>

          {list.description && (
            <p className="text-muted-foreground mt-2">{list.description}</p>
          )}

          {/* Author */}
          <Link to={`/${npub}`} className="inline-flex items-center gap-2 mt-4 group">
            <Avatar className="size-6">
              <AvatarImage src={author.data?.metadata?.picture} />
              <AvatarFallback className="text-xs">{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
              {displayName}
            </span>
          </Link>

          {/* Tags */}
          {list.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {list.hashtags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleShare}>
              <ShareIcon className="size-3.5" />
              Share
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleFork}>
              <GitForkIcon className="size-3.5" />
              Fork
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleShare}>
              <CopyIcon className="size-3.5" />
              Copy Link
            </Button>
            {isOwner && (
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to={`/edit/${list.dTag}?kind=${list.kind}`}>
                  <PencilIcon className="size-3.5" />
                  Edit
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="space-y-3">
          {list.items.map((item, i) => (
            <ItemRow key={i} item={item} />
          ))}
        </div>

        {list.items.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">This list has no items yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
